import { useQuery } from '@tanstack/react-query';
import { Users, Package } from 'lucide-react';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { useT } from '@/lib/i18n/useT';
import type { CoContactListResponse, XkStock } from '@/lib/api/types';

/**
 * PRD Xotski v2.0 §7.1 — Entity Home KPI strip.
 *
 * Four metrics, in this order:
 *   1. Moments captured     — completed consumer journeys (scan→claim).
 *   2. Throughput           — people / hour (PRD lane discipline metric).
 *   3. Inventory health     — % of items above low-stock threshold.
 *   4. Contacts captured    — total deduped contacts in CRM.
 *
 * Cost Per Moment, ad-equivalent value and any spend-derived number
 * stay OUT (PRD §2 lane discipline — those belong to Proxity / Xamplin).
 *
 * Task 8.1 — a card only appears once it has something to say.
 *
 * Moments and Throughput need aggregation endpoints that do not exist yet;
 * they used to render as permanent em-dash placeholders, which is two thirds of
 * a "wall of zeros" on every workspace, forever. They are gone until their data
 * is real. The remaining two hide themselves while empty, and when nothing has
 * a value the strip renders nothing at all so the page can show an invitation
 * instead of a grid of blanks.
 */
export function EntityKpiStrip() {
  const t = useT();

  const contactsQ = useQuery({
    queryKey: ['kpi', 'contacts-total'],
    queryFn: () => api.get<CoContactListResponse>('/co-contacts', { params: { page: 1, limit: 1 } }),
  });
  const stockQ = useQuery({
    queryKey: queryKeys.stock.all,
    queryFn: () => api.list<XkStock>('/xk-stock'),
  });

  const contactsTotal = contactsQ.data?.total ?? null;
  const stockItems = stockQ.data?.items ?? [];
  const inventoryHealth = computeInventoryHealth(stockItems);

  const loading = stockQ.isLoading || contactsQ.isLoading;

  // A KPI earns its card by having a number. `0` is a number worth showing —
  // "0 contacts captured" is real news on a running event — but a metric with
  // nothing behind it is just furniture.
  const cards = [
    inventoryHealth != null && {
      key: 'inventoryHealth',
      icon: <Package className="h-4 w-4" />,
      label: t('entityHome.kpi.inventoryHealth'),
      value: `${inventoryHealth}%`,
      hint: t('entityHome.kpi.aboveThreshold'),
    },
    contactsTotal != null && {
      key: 'contacts',
      icon: <Users className="h-4 w-4" />,
      label: t('entityHome.kpi.contacts'),
      value: formatCount(contactsTotal),
      hint: t('entityHome.kpi.totalDeduped'),
    },
  ].filter(Boolean) as {
    key: string;
    icon: React.ReactNode;
    label: string;
    value: string;
    hint: string;
  }[];

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-24 rounded-xl" />
      </div>
    );
  }
  // Nothing to report yet — let the page invite the user instead.
  if (cards.length === 0) return null;

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {cards.map((c) => (
        <KpiCard
          key={c.key}
          icon={c.icon}
          label={c.label}
          value={c.value}
          hint={c.hint}
          isLoading={false}
        />
      ))}
    </div>
  );
}

function KpiCard({
  icon,
  label,
  value,
  hint,
  isLoading,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number | null;
  hint?: string;
  isLoading: boolean;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-[var(--color-fg-muted)]">
        <span className="text-[var(--color-accent-ink)]">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold tabular-nums text-[var(--color-fg-primary)]">
        {isLoading ? (
          <Skeleton className="h-8 w-20" />
        ) : value == null ? (
          <span className="text-[var(--color-fg-muted)]">—</span>
        ) : (
          value
        )}
      </div>
      {hint && (
        <p className="mt-1 text-xs text-[var(--color-fg-muted)]">{hint}</p>
      )}
    </Card>
  );
}

function computeInventoryHealth(items: XkStock[]): number | null {
  if (items.length === 0) return null;
  // PRD §9 — inventory health = % of items above low-stock threshold.
  // Without explicit per-item threshold we treat "0 remaining" as low.
  const healthy = items.filter((s) => (s.initialQuantity ?? 0) > 0).length;
  return Math.round((healthy / items.length) * 100);
}

function formatCount(n: number): string {
  if (n >= 10_000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
}
